import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Col, Form, Row, Select,
} from 'antd';
import { sortByObjProperty } from '../../common/helpers';
import { REMINDER_OPTIONS } from './reminder.constants';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import ExternalRecipientsSelector from './ExternalRecipientsSelector';
import { formSectionsPropType } from '../../forms/forms.propTypes';
import { getOption } from '../../common/hooks/useFormEmailOptions';
import { AdditionalItem, AdditionalLabel } from '../../forms/FormWorkflows/AssignmentDropdownItems';

const FORM_CUSTOMER_EMAIL_TITLE = "Email linked to form's customer";
const FORM_CUSTOMER_EMAIL_HELP = "The reminder will be sent to the office email address linked to the form's customer";

export const FORM_CUSTOMER_EMAIL_OPTION = {
  value: 'form-customer-email',
  title: FORM_CUSTOMER_EMAIL_TITLE,
  label: (
    <AdditionalLabel
      title={FORM_CUSTOMER_EMAIL_TITLE}
      help={FORM_CUSTOMER_EMAIL_HELP}
    />
  ),
  children: (
    <AdditionalItem
      title={FORM_CUSTOMER_EMAIL_TITLE}
      help={FORM_CUSTOMER_EMAIL_HELP}
    />
  ),
};

const FORM_CONFIG_TEXT_TYPES = new Set(['text', 'attribute']);

/**
 * Dynamic field which allows user to add an arbitrary number of inputs to an Antd Form
 */
const DynamicRemindersField = function ({
  form,
  type = 'reminders',
  isDisplay = false,
  selectedDivisionId,
  selectedProjectId,
  visible,
  isFormConfig = false,
  sections = [],
}) {
  const defaultReminder = REMINDER_OPTIONS.DEFAULT_REMINDER;
  const selectOptions = Object.values(REMINDER_OPTIONS);
  const projects = useSelector((state) => state.projects.projects);
  const customers = useSelector((state) => state.customers.customers);

  const formConfigExternalOptions = useMemo(() => {
    if (type !== 'externalReminders' || (!isFormConfig && !sections.length)) return [];

    const opts = [FORM_CUSTOMER_EMAIL_OPTION];
    sections.forEach((section) => {
      (section.fields || []).forEach((field) => {
        const {
          id,
          selectedType,
          configProps: { title },
        } = field;

        if (!FORM_CONFIG_TEXT_TYPES.has(selectedType) || !title) return;
        const { id: fieldId, ...option } = getOption({
          fieldId: id,
          title,
          type: 'reminder',
        });
        opts.push({
          ...option,
          value: fieldId,
        });
      });
    });
    return opts;
  }, [isFormConfig, type, sections]);

  const sortedExternalRecipientChoices = useMemo(() => {
    if (isFormConfig || type !== 'externalReminders') return [];
    const opts = [];

    projects.forEach((project) => {
      if (!project.billingContact || !project.active) return;
      opts.push({
        id: project.id,
        type: 'project',
        divisionId: project.divisionId,
        value: `${project.id}-project`,
        label: `${project.name} - Billing Email`,
      });
    });

    Object.values(customers).forEach((customer) => {
      const divisionIdSet = customer.officeEmail || customer.billingEmail
        ? new Set(customer.divisionIds)
        : new Set();
      if (customer.officeEmail) {
        opts.push({
          id: customer.id,
          type: 'customer',
          divisionIds: divisionIdSet,
          value: `${customer.id}-office`,
          label: `${customer.name} - Office Email`,
        });
      }
      if (customer.billingEmail) {
        opts.push({
          id: customer.id,
          type: 'customer',
          divisionIds: divisionIdSet,
          value: `${customer.id}-billing`,
          label: `${customer.name} - Billing Email`,
        });
      }
    });

    return opts.sort(sortByObjProperty('label'));
  }, [isFormConfig, type, projects, customers]);

  const externalRecipientOptions = useMemo(() => {
    if (isFormConfig) return formConfigExternalOptions;
    if (type !== 'externalReminders' || !visible) return [];
  
    // Include form-config options so preconfigured shift table recipients show labels
    const opts = [...formConfigExternalOptions];
    sortedExternalRecipientChoices.forEach((choice) => {
      if (choice.type === 'project') {
        if (
          (selectedProjectId && choice.id !== selectedProjectId)
          || choice.divisionId !== selectedDivisionId
        ) return;
        opts.push(choice);
      } else {
        // Customer
        if (!choice.divisionIds.has(selectedDivisionId)) return;
        opts.push(choice);
      }
    });
    return opts;
  }, [
    isFormConfig,
    formConfigExternalOptions,
    sortedExternalRecipientChoices,
    type,
    visible,
    selectedDivisionId,
    selectedProjectId,
  ]);

  const title = type === 'reminders' ? 'Reminders' : 'External Reminders';

  return (
    <Form.List name={type}>
      {(fields, { add, remove }) => (
        <Col>
          <Row style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}>
            {title}
          </Row>
          {fields.map((field, index) => (
            <div key={field.key} className="schedule-form-reminder">
              <Row>
                <Col style={{ width: 20, marginTop: 15 }}>
                  { index !== 0 && <MinusCircleOutlined onClick={() => remove(field.name)} /> }
                </Col>
                <Col>
                  <Form.Item
                    name={type === 'externalReminders' ? [field.name, 'value'] : undefined}
                    {...(type === 'reminders' ? field : {})}
                    style={{ marginRight: 4 }}
                    className="schedule-form-reminder-item"
                  >
                    <Select
                      style={{ width: 175, margin: '10px 0px' }}
                      labelInValue
                      defaultValue={defaultReminder}
                      options={selectOptions}
                      disabled={isDisplay}
                    />
                  </Form.Item>
                </Col>
                <Col style={{ width: 500 }}>
                  {type === 'externalReminders' && (
                    <Form.Item
                      name={[field.name, 'recipients']}
                      style={{ marginRight: 4 }}
                      className="schedule-form-reminder-item"
                    >
                      <ExternalRecipientsSelector
                        isDisplay={isDisplay}
                        recipientOptions={externalRecipientOptions}
                        reminderValue={form?.current?.getFieldValue([type, field.name, 'value'])?.value}
                      />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </div>
          ))}
          <Row>
            <Form.Item>
              <OnTraccrButton
                title="Add Reminder"
                onClick={add}
                style={{ width: 200, marginLeft: 20 }}
                icon={<PlusOutlined />}
              />
            </Form.Item>
          </Row>
        </Col>
      )}
    </Form.List>
  );
};

DynamicRemindersField.propTypes = {
  type: PropTypes.string.isRequired,
  isDisplay: PropTypes.bool,
  selectedDivisionId: PropTypes.string,
  selectedProjectId: PropTypes.string,
  form: PropTypes.shape({}),
  visible: PropTypes.bool,
  isFormConfig: PropTypes.bool,
  sections: formSectionsPropType,
};

DynamicRemindersField.defaultProps = {
  isDisplay: false,
  selectedDivisionId: undefined,
  selectedProjectId: undefined,
  form: undefined,
  visible: false,
  isFormConfig: false,
  sections: [],
};

export default DynamicRemindersField;
